import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm, Row, Col } from 'antd';
import FormSelect from '../../../../../../antd/form/FormSelect/FormSelect';
import { required } from '../../../../../../antd/form/FormValidation';
import FormButton from '../../../../../../antd/form/FormButton/FormButton';
import FormButtonsContainer from '../../../../../../antd/form/FormButtonsContainer/FormButtonsContainer';
import Form from '../../../../../../antd/form/Form/Form';
import FormTextField from '../../../../../../antd/form/FormTextField/FormTextField';
import FormNumberField from '../../../../../../antd/form/FormNumberField/FormNumberField';
import FormDate from '../../../../../../antd/form/FormDate/FormDate';
import FormAcademicYear from '../../../../../../antd/form/FormAcademicYear/FormAcademicYear';
import { CollapseBlock } from '../../../../../../common/index';
// import dayjs from 'dayjs';

const ErasmusChallengesForm = ({
  onSubmit,
  initials,
  isPreview,
  isEdit,
  consortiums,
  universities,
  studyPrograms,
  erasmusMobilityTypes,
  erasmusMobilitySubtypes,
}) => {
  const { t } = useTranslation();
  const [form] = AntdForm.useForm();

  const consortiumOptions = useMemo(
    () =>
      consortiums?.map((item) => ({
        key: item.id,
        value: item.id,
        label: item.name,
      })) || [],
    [consortiums],
  );

  const universityOptions = useMemo(
    () =>
      universities?.map((item) => ({
        key: item.id,
        value: item.id,
        label: item.name,
      })) || [],
    [universities],
  );

  const studyDegreeOptions = useMemo(
    () => [
      { key: 'Bc.', value: 'Bc.', label: 'Bc.' },
      { key: 'Ing.', value: 'Ing.', label: 'Ing.' },
      { key: 'PhD.', value: 'PhD.', label: 'PhD.' },
    ],
    [],
  );

  const studyProgramOptions = useMemo(
    () =>
      studyPrograms?.map((item) => ({
        key: item.id,
        value: item.id,
        label: item.name,
      })) || [],
    [studyPrograms],
  );

  const handleSubmit = (values) => {
    const cleanedValues = {
      ...values,
      deadline: values.deadline ? values.deadline.format('YYYY-MM-DD') : null,
      // Ensure only one of consortium_id or university_id is set
      consortium_id: values.consortium_id || null,
      university_id: values.university_id || null,
      mobility_type_id: values.mobility_type_id ?? null,
      mobility_subtype_id: values.mobility_subtype_id ?? null,
      study_year: values.study_year ?? null,
      study_program_id: values.study_program_id ?? null,
      study_degree: values.study_degree ?? null,
    };
    onSubmit(cleanedValues);
  };

  // Watch consortium_id and university_id to ensure only one is selected
  const consortiumId = AntdForm.useWatch('consortium_id', form);
  const universityId = AntdForm.useWatch('university_id', form);
  const mobilityTypeId = AntdForm.useWatch('mobility_type_id', form);

  const mobilityTypeOptions = useMemo(
    () =>
      erasmusMobilityTypes?.map((item) => ({
        key: item.id,
        value: item.id,
        label: item.name,
      })) || [],
    [erasmusMobilityTypes],
  );

  const mobilitySubtypeOptions = useMemo(() => {
    if (!mobilityTypeId || !erasmusMobilitySubtypes) return [];
    return erasmusMobilitySubtypes
      .filter((sub) => sub.type_id === mobilityTypeId)
      .map((item) => ({
        key: item.id,
        value: item.id,
        label: item.name,
      }));
  }, [mobilityTypeId, erasmusMobilitySubtypes]);

  useEffect(() => {
    if (consortiumId && universityId) {
      // If both are selected, clear the one that was just selected
      // This is a simple approach - in practice, you might want more sophisticated logic
      form.setFieldsValue({ university_id: null });
    }
  }, [consortiumId, universityId, form]);

  return (
    <Form onFinish={handleSubmit} initialValues={initials} form={form}>
      <CollapseBlock title={t('Základné údaje')} collapsible="false">
        <FormTextField
          label={t('Názov')}
          name={'name'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <FormDate
              label={t('Termín')}
              name={'deadline'}
              rules={[required(t)]}
              disabled={isPreview}
              // disabledDate={(current) => current && current < dayjs().startOf('day')}
              disabledDate={undefined}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={12}>
            <FormAcademicYear
              label={t('Akademický rok')}
              name={'academic_year'}
              rules={[required(t)]}
              disabled={isPreview}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <FormSelect
              label={t('Typ mobility')}
              name={'mobility_type_id'}
              placeholder={t('Vybrať typ mobility')}
              options={mobilityTypeOptions}
              disabled={isPreview}
              allowClear
            />
          </Col>
          <Col span={12}>
            <FormSelect
              label={t('Podtyp mobility')}
              name={'mobility_subtype_id'}
              placeholder={t('Vybrať podtyp mobility')}
              options={mobilitySubtypeOptions}
              disabled={isPreview || !mobilityTypeId}
              allowClear
            />
          </Col>
        </Row>
      </CollapseBlock>

      <CollapseBlock title={t('Viazanie na konzorcium alebo univerzitu')} collapsible="false">
        <FormSelect
          label={t('Konzorcium')}
          name={'consortium_id'}
          placeholder={t('Vybrať konzorcium')}
          options={consortiumOptions}
          disabled={isPreview || !!universityId}
          allowClear
        />
        <FormSelect
          label={t('Univerzita')}
          name={'university_id'}
          placeholder={t('Vybrať univerzitu')}
          options={universityOptions}
          disabled={isPreview || !!consortiumId}
          allowClear
        />
      </CollapseBlock>

      <CollapseBlock title={t('Voliteľné kritériá')} collapsible="false">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <FormNumberField
              label={t('Ročník štúdia')}
              name={'study_year'}
              type="number"
              disabled={isPreview}
              placeholder={t('Voliteľné')}
              min={1}
              max={3}
            />
          </Col>
          <Col xs={24} md={8}>
            <FormSelect
              label={t('Študijný program')}
              name={'study_program_id'}
              placeholder={t('Vybrať študijný program')}
              options={studyProgramOptions}
              disabled={isPreview}
              allowClear
            />
          </Col>
          <Col xs={24} md={8}>
            <FormSelect
              label={t('Stupeň štúdia')}
              name={'study_degree'}
              placeholder={t('Vybrať stupeň štúdia')}
              options={studyDegreeOptions}
              disabled={isPreview}
              allowClear
            />
          </Col>
        </Row>
      </CollapseBlock>

      {isPreview ? null : (
        <FormButtonsContainer>
          <FormButton submitOnClick={true} submitKey={'erasmusChallenges'}>
            {t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      )}
    </Form>
  );
};

export default ErasmusChallengesForm;
